import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

export interface ClientCommunication {
  id: string;
  horseId: string;
  type: string;
  dateTime: string;
  notes: string;
  staffMember?: string;
  /** The email this entry logged (INV-125): null for an entry logged by hand, absent offline. */
  outboundEmail?: { status: string; openedAt: string | null; bouncedAt: string | null } | null;
}

interface ClientCommunicationDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (communication: Omit<ClientCommunication, 'id'>) => void | Promise<unknown>;
  horseId: string;
  editingCommunication?: ClientCommunication | null;
}

const communicationTypes = [
  'Phone',
  'Email',
  'In-person',
  'Text Message',
  // Written by the reminder scheduler's SMS pass (ADR-0022) — present here so
  // editing a scheduler-logged row doesn't seed the Select out-of-range.
  'SMS',
  'Video Call',
  // Written when a StableDocs packet is emailed; listed so editing that row stays in range.
  'StableDocs',
  'Other',
];

const ClientCommunicationDialog: React.FC<ClientCommunicationDialogProps> = ({
  open,
  onClose,
  onSave,
  horseId,
  editingCommunication,
}) => {
  const [formData, setFormData] = useState({
    type: 'Phone',
    dateTime: new Date(),
    notes: '',
  });
  // Save is awaited (close-only-on-success), which opens a double-submit
  // window — disable the button while the save is in flight.
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (editingCommunication) {
      setFormData({
        type: editingCommunication.type,
        dateTime: new Date(editingCommunication.dateTime),
        notes: editingCommunication.notes,
      });
    } else {
      setFormData({
        type: 'Phone',
        dateTime: new Date(),
        notes: '',
      });
    }
  }, [editingCommunication, open]);

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await onSave({
        horseId,
        type: formData.type,
        dateTime: formData.dateTime.toISOString(),
        notes: formData.notes,
      });
      handleClose(); // only close on success — keep the typed note if the save fails
    } catch {
      // Save failed (e.g. the offline durability gate). Keep the dialog open so
      // the entered communication isn't lost; the error is surfaced by the
      // caller's onError (snackbar).
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      type: 'Phone',
      dateTime: new Date(),
      notes: '',
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {editingCommunication ? 'Edit' : 'Log'} Client Communication
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            select
            label="Communication Type"
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
            fullWidth
          >
            {communicationTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </TextField>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateTimePicker
              label="Date & Time"
              value={formData.dateTime}
              onChange={(newValue) => {
                if (newValue) {
                  setFormData({ ...formData, dateTime: newValue });
                }
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                },
              }}
            />
          </LocalizationProvider>

          <TextField
            label="Notes"
            multiline
            rows={6}
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            fullWidth
            placeholder="Describe the communication with the client (e.g., owner called about horse's health, discussed treatment plan, etc.)"
            required
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!formData.notes.trim() || submitting}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClientCommunicationDialog;

